import { AlertOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Form, Switch, Tag, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { authRequest } from "../../../../common/request/authRequest";
import { useAuth } from "../../../../store/AuthContext/AuthContext";
import { SpinLZ } from "../../../components/design/Spinner/SpinLZ";
import { ButtonLZ } from "../../../components/design/Buttons/ButtonLZ";
import { PageTitle } from "../../common/PageTitle";
import { EmailRecipientsModal } from "./EmailRecipientsModal";
import './AlertConfig.css';

type AlertConfigResponse = {
	project: string;
	emails: string[];
	schedulePost: boolean;
	googleChanges: boolean;
	googleChangesWords: string[];
	reviewsAttack: boolean;
};

type AlertOptionsFormData = {
	schedulePost: boolean;
	googleChanges: boolean;
	googleChangesWords: string[];
	reviewsAttack: boolean;
};

// Tiene que coincidir con SyncLocationChangeType.java
const GOOGLE_CHANGES_CRITERIA = [
	{ label: 'Nombre', value: 'TITLE' },
	{ label: 'Web', value: 'WEBSITE' },
	{ label: 'Teléfonos', value: 'PHONES' },
	{ label: 'Categorías', value: 'CATEGORIES' },
	{ label: 'Servicios', value: 'SERVICES' },
	{ label: 'Horarios', value: 'HOURS' }
];

const ALERT_OPTIONS = [
	{
		key: 'reviewsAttack' as const,
		title: 'Aumento inusual de reseñas negativas',
		description: 'Alerta de los locales con un incremento inusual de reseñas negativas en las últimas 24 horas, como en el caso de un ataque de reputación.',
	},
	{
		key: 'googleChanges' as const,
		title: 'Cambios externos en la ficha de Google',
		description: 'Alerta de los locales con cambios externos en la ficha de Google, por ejemplo cuando Google cambia un horario por una sugerencia de un cliente.',
	},
	{
		key: 'schedulePost' as const,
		title: 'Ejecución de publicaciones programadas',
		description: 'Recibe alertas cuando se ejecute la publicación de tus posts programados para confirmar su éxito o revisar errores.',
	}
];

const compareStringArrays = (a: string[] = [], b: string[] = []) => {
	if (a.length !== b.length) return false;
	const sortedA = [...a].sort();
	const sortedB = [...b].sort();
	return sortedA.every((value, index) => value === sortedB[index]);
};

const getDirtyAlertFields = (current: AlertOptionsFormData | undefined, saved: AlertOptionsFormData | null) => {
	const dirty = new Set<string>();
	if (!current || !saved) return dirty;

	if (current.schedulePost !== saved.schedulePost) dirty.add('schedulePost');
	if (current.googleChanges !== saved.googleChanges) dirty.add('googleChanges');
	if (!compareStringArrays(current.googleChangesWords, saved.googleChangesWords)) dirty.add('googleChangesWords');
	if (current.reviewsAttack !== saved.reviewsAttack) dirty.add('reviewsAttack');

	return dirty;
};

const isAlertCardDirty = (key: string, dirtyFields: Set<string>) => {
	if (dirtyFields.has(key)) return true;
	if (key === 'googleChanges' && dirtyFields.has('googleChangesWords')) return true;
	return false;
};

export const AlertConfig = () => {
	const { projectGroup } = useAuth();
	const [form] = Form.useForm<AlertOptionsFormData>();
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [showEmailsModal, setShowEmailsModal] = useState(false);
	const [emails, setEmails] = useState<string[]>([]);
	const [savedAlertOptions, setSavedAlertOptions] = useState<AlertOptionsFormData | null>(null);
	const googleChangesEnabled = Form.useWatch('googleChanges', form);
	const formValues = Form.useWatch([], form) as AlertOptionsFormData | undefined;
	const dirtyFields = useMemo(() => getDirtyAlertFields(formValues, savedAlertOptions), [formValues, savedAlertOptions]);
	const hasUnsavedChanges = dirtyFields.size > 0;

	useEffect(() => {
		setIsLoading(true);
		setSavedAlertOptions(null);
		authRequest(`${process.env.REACT_APP_API}/projects/${projectGroup.project}/alert-config`, {})
			.then((response: AlertConfigResponse) => {
				form.setFieldsValue({
					schedulePost: response.schedulePost,
					googleChanges: response.googleChanges,
					googleChangesWords: response.googleChangesWords,
					reviewsAttack: response.reviewsAttack,
				});
				setEmails(response.emails ?? []);
				setSavedAlertOptions({
					schedulePost: response.schedulePost,
					googleChanges: response.googleChanges,
					googleChangesWords: response.googleChangesWords,
					reviewsAttack: response.reviewsAttack,
				});
			})
			.catch(() => {
				message.error('Error al cargar la configuración de alertas');
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [projectGroup.project, form]);

	const onFinish = async (values: AlertOptionsFormData) => {
		if (!hasUnsavedChanges) return;

		setIsSaving(true);
		try {
			await authRequest(`${process.env.REACT_APP_API}/projects/${projectGroup.project}/alert-config/items`, {
				method: 'PATCH',
				data: values,
			});
			setSavedAlertOptions(values);
			message.success('Configuración guardada correctamente');
		} catch {
			message.error('Error al guardar la configuración');
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Card className='main-card-content'>
			<div className='alert-config-header'>
				<div className='alert-config-header-main'>
					<PageTitle
						title='Configuración de Alertas'
						pageInfo='Permite activar una serie de alertas para ser recibidas por email, estas alertas tienen en común la necesidad de ser resueltas de forma rápida por parte de tu equipo.'
					/>
				</div>
				{!isLoading && (
					<div className={`alert-recipients-panel ${emails.length === 0 ? 'alert-recipients-panel--empty' : ''}`}>
						<div className='alert-recipients-panel-header'>
							<Button
								type='link'
								icon={<SettingOutlined />}
								size='small'
								onClick={() => setShowEmailsModal(true)}
								className='alert-recipients-action'
							>
								{emails.length === 0 ? 'Configurar destinatarios' : 'Gestionar Destinatarios'}
							</Button>
						</div>
						<div className='alert-recipients-list'>
							{emails.length > 0 ? (
								emails.map((email) => (
									<span key={email} className='alert-recipient-item'>
										{email}
									</span>
								))
							) : (
								<button
									type='button'
									className='alert-recipients-empty'
									onClick={() => setShowEmailsModal(true)}
								>
									Sin emails configurados — haz clic para añadir
								</button>
							)}
						</div>
					</div>
				)}
			</div>

			{isLoading ? (
				<SpinLZ />
			) : (
				<Form form={form} layout='vertical' onFinish={onFinish} className='alert-config-form'>
					<EmailRecipientsModal
						visible={showEmailsModal}
						onClose={() => setShowEmailsModal(false)}
						emails={emails}
						onSaved={setEmails}
					/>

					{ALERT_OPTIONS.map(({ key, title, description }) => (
						<Card
							key={key}
							className={`alert-option-card ${isAlertCardDirty(key, dirtyFields) ? 'alert-option-card--dirty' : ''}`}
							bordered
						>
							<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
								<div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
									<AlertOutlined style={{ fontSize: 20, color: '#fa8c16', marginTop: 2, flexShrink: 0 }} />
									<div>
										<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
											<span style={{ fontWeight: 500 }}>{title}</span>
											{isAlertCardDirty(key, dirtyFields) && (
												<Tag className='alert-pending-badge'>Sin guardar</Tag>
											)}
										</div>
										<div style={{ color: 'grey', fontSize: 13, marginTop: 4 }}>{description}</div>
									</div>
								</div>
								<Form.Item name={key} valuePropName='checked' style={{ marginBottom: 0 }}>
									<Switch checkedChildren='Activada' unCheckedChildren='Desactivada' />
								</Form.Item>
							</div>

							{key === 'googleChanges' && (
								<div className={`alert-keywords-section ${googleChangesEnabled ? 'alert-keywords-section--editable' : 'alert-keywords-section--readonly'}`}>
									<div className='alert-keywords-label'>Campos a monitorizar</div>
									<div className='alert-keywords-hint'>Segundo criterio: selecciona qué cambios en la ficha deben generar alerta.</div>
									<Form.Item
										name='googleChangesWords'
										dependencies={['googleChanges']}
										style={{ marginBottom: 0 }}
										rules={[
											{
												validator: async (_, value) => {
													if (!form.getFieldValue('googleChanges')) return;
													if (!value || value.length === 0) {
														throw new Error('Selecciona al menos un campo a monitorizar');
													}
												},
											},
										]}
									>
										<Checkbox.Group
											options={GOOGLE_CHANGES_CRITERIA}
											disabled={!googleChangesEnabled}
											className='alert-keywords-checkbox-group'
										/>
									</Form.Item>
								</div>
							)}
						</Card>
					))}

					<div className='alert-config-actions'>
						{hasUnsavedChanges && (
							<span className='alert-unsaved-notice'>Hay cambios pendientes de guardar</span>
						)}
						<ButtonLZ type='primary' onClick={() => form.submit()} disabled={isSaving || !hasUnsavedChanges}>
							Guardar
						</ButtonLZ>
					</div>
				</Form>
			)}
		</Card>
	);
};
